import { createContext, Suspense, useCallback, useContext, useMemo, useState } from 'react';
import { cx } from 'class-variance-authority';

import { Button, Spinner, Tag, Text } from '@nvision/ui';

import styles from './card.module.css';

interface CardContextValue {
  collapsible: boolean;
  isOpen: boolean;
  toggle: () => void;
}

export const CardContext = createContext<CardContextValue>({
  collapsible: false,
  isOpen: false,
  toggle: () => {},
});

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  collapsible?: boolean;
  defaultIsOpen?: boolean;
}

export const Card: React.FC<CardProps> = ({
  children,
  className,
  collapsible = false,
  defaultIsOpen = true,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(defaultIsOpen);

  const toggle = useCallback(() => {
    if (!collapsible) {
      return;
    }

    setIsOpen((prev) => !prev);
  }, [collapsible]);

  const contextValue = useMemo(
    () => ({
      collapsible,
      isOpen,
      toggle,
    }),
    [collapsible, isOpen, toggle],
  );

  return (
    <CardContext.Provider value={contextValue}>
      <div {...props} className={cx(styles.card, className)}>
        {children}
      </div>
    </CardContext.Provider>
  );
};

interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  title?: string;
  actions?: React.ReactNode;
  errorMessage?: string;
}

export const CardHeader: React.FC<CardHeaderProps> = ({
  children,
  className,
  title,
  actions,
  errorMessage,
  onClick,
  onKeyDown,
  ...props
}) => {
  const cardContext = useContext(CardContext);

  const isCollapsible = cardContext.collapsible;
  const isOpen = cardContext.isOpen;

  return (
    <div
      {...props}
      className={cx(styles.cardHeader, { [styles.cardHeaderCollapsed]: !isOpen && isCollapsible }, className)}
    >
      <div className={styles.headerContent}>
        <div className={styles.title}>
          {isCollapsible && (
            <Button
              ghost
              secondary
              icon="chevron-down"
              onPress={() => cardContext.toggle()}
              className={cx(styles.chevronButton, { [styles.chevronButtonCollapsed]: !isOpen })}
            />
          )}
          {title && (
            <Text size="sm" weight="semibold">
              {title}
            </Text>
          )}
          {errorMessage && <Tag destructive mark ghost hint={errorMessage} />}
          {children}
        </div>

        {actions && <div className={styles.headerActions}>{actions}</div>}
      </div>
    </div>
  );
};

interface CardMainProps extends React.HTMLAttributes<HTMLDivElement> {}

export const CardMain: React.FC<CardMainProps> = ({ children, className, ...props }) => {
  const cardContext = useContext(CardContext);

  const isCollapsible = cardContext.collapsible;
  const isOpen = cardContext.isOpen;

  return (
    <div
      {...props}
      className={cx(styles.cardMain, { [styles.cardMainCollapsed]: !isOpen && isCollapsible }, className)}
    >
      <div className={styles.cardMainInner}>
        <Suspense fallback={<Spinner />}>{children}</Suspense>
      </div>
    </div>
  );
};

interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {}

export const CardFooter: React.FC<CardFooterProps> = ({ children, className, ...props }) => {
  return (
    <div {...props} className={cx(styles.cardFooter, className)}>
      {children}
    </div>
  );
};

interface FieldProps {
  horizontal?: boolean;
  children: React.ReactNode;
}

// TODO: tests
export const CardField: React.FC<FieldProps> = ({ horizontal = false, children }) => {
  return <div className={cx(styles.field, { [styles.horizontalField]: horizontal })}>{children}</div>;
};
